/**
 * How a finish relates to a RAL code. Display-only; never used to derive HEX.
 * - closest: a nearest RAL is recorded
 * - varies: manufacturers disagree on a RAL equivalent
 * - none: no RAL equivalent (or no single agreed equivalent)
 * - custom: user-entered RAL + HEX
 */
export type RalRelationship = 'closest' | 'varies' | 'none' | 'custom';

/** How the finish is classified. Rendering still uses HEX; grain/foil is later. */
export type FinishFamily = 'paint' | 'wood' | 'metal' | 'custom';

/**
 * Reusable product colour: one record can be assigned to a frame, slab,
 * sash, cill, trim, or any later configurable component.
 *
 * RAL is ceremonial display metadata. HEX is the only rendering source of truth.
 * Never derive HEX from a RAL code.
 */
export interface ProductColour {
  id: string;
  name: string;
  ral?: string;
  ralRelationship: RalRelationship;
  /** Overrides the default sentence for `none` (e.g. no single agreed equivalent). */
  ralNote?: string;
  hex: string;
}

export function formatRalDisplay(
  colour: ProductColour,
  customRalText?: string
): string {
  switch (colour.ralRelationship) {
    case 'closest':
      return colour.ral ? `Closest match to ${colour.ral}` : 'Closest RAL match';
    case 'varies':
      return 'RAL equivalents vary by manufacturer';
    case 'none':
      return colour.ralNote ?? 'No RAL equivalent';
    case 'custom': {
      const entered = customRalText?.trim();
      return entered && entered.length > 0 ? entered : 'User-entered RAL + HEX';
    }
    default:
      return colour.ral ?? 'No RAL equivalent';
  }
}

const HEX6 = /^#([0-9A-Fa-f]{6})$/;
const HEX3 = /^#([0-9A-Fa-f]{3})$/;

export function isValidHex(value: string): boolean {
  const trimmed = value.trim();
  return HEX6.test(trimmed) || HEX3.test(trimmed);
}

/** Returns canonical `#RRGGBB` or null if the value is not a hex colour. */
export function normaliseHex(value: string): string | null {
  const trimmed = value.trim();
  const six = trimmed.match(HEX6);
  if (six) return `#${six[1].toUpperCase()}`;
  const three = trimmed.match(HEX3);
  if (!three) return null;
  const [r, g, b] = three[1].split('');
  return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
}

export interface RgbColour {
  r: number;
  g: number;
  b: number;
}

export function hexToRgb(hex: string): RgbColour | null {
  const normalised = normaliseHex(hex);
  if (!normalised) return null;
  return {
    r: parseInt(normalised.slice(1, 3), 16),
    g: parseInt(normalised.slice(3, 5), 16),
    b: parseInt(normalised.slice(5, 7), 16),
  };
}

export function rgbToHex(rgb: RgbColour): string {
  const channel = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0')
      .toUpperCase();
  return `#${channel(rgb.r)}${channel(rgb.g)}${channel(rgb.b)}`;
}

/** Relative luminance 0–1 (sRGB). */
export function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const linear = (channel: number) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(rgb.r) + 0.7152 * linear(rgb.g) + 0.0722 * linear(rgb.b);
}
